import React, { FC } from 'react';

import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { formatPrice } from '@shared/utils/formatPrice';

interface IProps {
  image: string;
  name: string;
  priceRegular: number;
  priceRetail: number;
  type: GiftItemInfoType;
  tags?: string[];
  isSelectable?: boolean;
  choiceUnitCount?: number;
  brandName?: string;
}

const GiftItemPreview: FC<IProps> = ({
  image,
  name,
  priceRegular,
  priceRetail,
  type,
  tags,
  isSelectable,
  choiceUnitCount,
  brandName,
}: IProps) => {
  const discountRate =
    priceRegular > 0
      ? Math.round(((priceRegular - priceRetail) / priceRegular) * 100)
      : 0;

  return (
    <div className="relative flex h-full flex-col overflow-hidden rounded-xl text-base leading-5 shadow-md ring-1 ring-black/5 theme-bg-1">
      {/* 상품 사진 */}
      <div className="relative">
        <img
          src={image}
          alt={name}
          className="aspect-square w-full object-cover object-center"
        />
        {/* 뱃지 영역 */}
        <div className="absolute left-0 top-0 p-1.5">
          <span
            className={`inline-block rounded-sm px-1.5 py-0.5 text-[11px] font-bold text-white shadow-sm ${
              isSelectable
                ? 'bg-gradient-to-r from-indigo-500 to-purple-500 ring-1 ring-indigo-400/50'
                : type === GiftItemInfoType.Delivery
                ? 'bg-blue-500 ring-1 ring-blue-400/50'
                : 'bg-purple-500 ring-1 ring-purple-400/50'
            }`}
          >
            {isSelectable
              ? `선택형 ${type === GiftItemInfoType.Coupon ? '쿠폰' : '배송'}`
              : type === GiftItemInfoType.Delivery
              ? '배송'
              : '쿠폰'}
          </span>
        </div>
        {/* 할인율 뱃지 */}
        {discountRate > 0 && (
          <div className="absolute right-1.5 top-1.5 rounded-sm bg-red-500 px-1.5 py-0.5 text-[11px] font-bold text-white shadow-sm ring-1 ring-red-400/50">
            {discountRate}%
          </div>
        )}
      </div>
      {/* 상품 정보 */}
      <div className="flex flex-1 flex-col px-2.5 pb-2.5 pt-2">
        {/* 브랜드 */}
        {brandName && (
          <span className="mb-0.5 text-[11px] font-medium text-orange-600">
            {brandName}
          </span>
        )}
        {/* 상품명 */}
        <h4 className="line-clamp-2 text-sm font-bold leading-tight">{name}</h4>
        {/* 가격 */}
        <div className="mt-1.5 flex items-baseline gap-1">
          {discountRate > 0 && (
            <span className="text-sm font-extrabold text-red-500">{discountRate}%</span>
          )}
          <span className="text-[15px] font-extrabold">{formatPrice(priceRetail)}</span>
          <span className="text-[11px] text-gray-500">원</span>
          {priceRetail !== priceRegular && priceRegular > 0 && (
            <span className="text-[10px] text-gray-400 line-through">
              {formatPrice(priceRegular)}원
            </span>
          )}
        </div>
        {/* 태그 */}
        {tags && tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {tags.map((tag, i) => (
              <span
                key={`${tag}-${i}`}
                className="rounded-full bg-teal-500 px-2 py-0.5 text-[11px] font-bold text-white ring-1 ring-teal-400/50"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(GiftItemPreview);
